/**
 * istanbul ignore file
 *
 * @format
 */

import { Touches } from './touches';
import { EventEmitter } from '@angular/core';

export interface IvyPinchProperties {
	autoHeight?: boolean;
	autoZoomOut?: boolean;
	disablePan?: boolean;
	element: HTMLElement;
	eventHandler?: EventEmitter<{ detail?: unknown; name: string }>;
	limitPan?: boolean;
	limitZoom?: string | number;
	minPanScale?: number;
	minScale?: number;
	transitionDuration?: number;
}

export const IvyPinchDefaultProperties = {
	transitionDuration: 200,
	limitZoom: 'original image size',
	minScale: 0,
	minPanScale: 1.0001
};

export class IvyPinch {
	properties: IvyPinchProperties;
	touches: Touches;
	element: HTMLElement;
	elementTarget: string;
	parentElement: HTMLElement;
	i = 0;
	public scale = 1;
	initialScale = 1;
	elementPosition: DOMRect;
	eventType: string;
	startX = 0;
	startY = 0;
	moveX = 0;
	moveY = 0;
	initialMoveX = 0;
	initialMoveY = 0;
	moveXC = 0;
	moveYC = 0;
	draggingMode = false;
	distance = 0;
	initialDistance = 0;
	maxHtmlContentScale = 3;
	maxScale = 3;

	constructor(properties: IvyPinchProperties) {
		this.element = properties.element;
		this.elementTarget = this.element.querySelector('*').tagName;
		this.parentElement = this.element.parentElement;
		this.properties = { ...IvyPinchDefaultProperties, ...properties };
		this.pollLimitZoom();

		this.touches = new Touches({
			element: properties.element,
			resize: properties.autoHeight
		});

		/* Init */
		this.setBasicStyles();

		/*
		 * Listeners
		 */

		this.touches.on('touchstart', this.handleTouchstart);
		this.touches.on('touchend', this.handleTouchend);
		this.touches.on('pan', this.handlePan);
		this.touches.on('pinch', this.handlePinch);
		if (this.properties.autoHeight) {
			this.touches.on('resize', this.handleResize);
		}
	}

	/* Custom events */

	emitEvent(properties: { detail?: unknown; name: string }) {
		/* Emit angular event */
		if (this.properties.eventHandler) {
			this.properties.eventHandler.emit({
				name: properties.name,
				detail: properties.detail
			});
		}
	}

	/* Touchstart */

	handleTouchstart = (event: TouchEvent) => {
		this.getElementPosition();

		if (this.eventType === undefined) {
			this.getTouchstartPosition(event);
		}

		this.emitEvent({
			name: 'touchstart'
		});
	};

	/* Touchend */

	handleTouchend = (event: TouchEvent) => {
		/* touchend */
		if (event.type === 'touchend') {
			this.i = 0;
			this.draggingMode = false;
			const touches = event.touches;

			// Min scale
			if (this.scale < 1) {
				this.scale = 1;
			}

			// Auto Zoom Out
			if (this.properties.autoZoomOut && this.eventType === 'pinch') {
				this.scale = 1;
			}

			// Align image
			if (
				this.eventType === 'pinch' ||
				(this.eventType === 'pan' && this.scale > this.properties.minPanScale)
			) {
				this.alignImage();
			}

			// Update initial values
			if (this.eventType === 'pinch' || this.eventType === 'pan') {
				this.updateInitialValues();
			}

			this.eventType = 'touchend';

			if (touches && touches.length === 0) {
				this.eventType = undefined;
			}
		}

		this.emitEvent({
			name: 'touchend'
		});
	};

	/*
	 * Handlers
	 */

	handlePan = (event: TouchEvent) => {
		if (this.scale < this.properties.minPanScale || this.properties.disablePan) {
			return;
		}

		event.preventDefault();

		if (!this.elementPosition) {
			return;
		}

		const { clientX, clientY } = this.getClientPosition(event);

		if (!this.eventType) {
			this.startX = clientX - this.elementPosition.left;
			this.startY = clientY - this.elementPosition.top;
		}

		this.eventType = 'pan';
		this.moveX = this.initialMoveX + (this.moveLeft(event, 0) - this.startX);
		this.moveY = this.initialMoveY + (this.moveTop(event, 0) - this.startY);

		if (this.properties.limitPan) {
			this.limitPanY();
			this.limitPanX();
		}

		this.emitEvent({
			name: 'pan',
			detail: {
				moveX: this.moveX,
				moveY: this.moveY
			}
		});
		this.transformElement(0);
	};

	handlePinch = (event: TouchEvent) => {
		event.preventDefault();

		if (!this.elementPosition) {
			return;
		}

		if (this.eventType === undefined || this.eventType === 'pinch') {
			const touches = event.touches;

			if (!this.eventType) {
				this.initialDistance = this.getDistance(touches);
				const moveLeft0 = this.moveLeft(event, 0);
				const moveLeft1 = this.moveLeft(event, 1);
				const moveTop0 = this.moveTop(event, 0);
				const moveTop1 = this.moveTop(event, 1);
				this.moveXC = (moveLeft0 + moveLeft1) / 2 - this.initialMoveX;
				this.moveYC = (moveTop0 + moveTop1) / 2 - this.initialMoveY;
			}

			this.eventType = 'pinch';
			this.distance = this.getDistance(touches);
			this.scale = this.initialScale * (this.distance / this.initialDistance);
			this.moveX =
				this.initialMoveX -
				((this.distance / this.initialDistance) * this.moveXC - this.moveXC);
			this.moveY =
				this.initialMoveY -
				((this.distance / this.initialDistance) * this.moveYC - this.moveYC);

			this.handleLimitZoom();

			if (this.properties.limitPan) {
				this.limitPanY();
				this.limitPanX();
			}

			this.emitEvent({
				name: 'pinch',
				detail: {
					scale: this.scale
				}
			});
			this.transformElement(0);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	handleResize = (_event: Event) => {
		this.setAutoHeight();
	};

	handleLimitZoom() {
		const limitZoom = this.maxScale;
		const minScale = this.properties.minScale || 0;

		if (this.scale > limitZoom || this.scale <= minScale) {
			const imageWidth = this.getImageWidth();
			const imageHeight = this.getImageHeight();
			const enlargedImageWidth = imageWidth * this.scale;
			const enlargedImageHeight = imageHeight * this.scale;
			const moveXRatio = this.moveX / (enlargedImageWidth - imageWidth);
			const moveYRatio = this.moveY / (enlargedImageHeight - imageHeight);

			if (this.scale > limitZoom) {
				this.scale = limitZoom;
			}

			if (this.scale <= minScale) {
				this.scale = minScale;
			}

			const newImageWidth = imageWidth * this.scale;
			const newImageHeight = imageHeight * this.scale;

			this.moveX = -Math.abs(moveXRatio * (newImageWidth - imageWidth));
			this.moveY = -Math.abs(-moveYRatio * (newImageHeight - imageHeight));
		}
	}

	getLimitZoom() {
		if (this.properties.limitZoom === 'original image size') {
			if (this.elementTarget === 'IMG') {
				const img = this.element.getElementsByTagName('img')[0];

				if (img.naturalWidth && img.offsetWidth) {
					this.maxScale = img.naturalWidth / img.offsetWidth;
					return this.maxScale;
				}
			} else {
				this.maxScale = this.maxHtmlContentScale;
				return this.maxScale;
			}
		} else {
			this.maxScale =
				typeof this.properties.limitZoom === 'number' ? this.properties.limitZoom : 0;
			return this.maxScale;
		}
	}

	moveLeft(event: TouchEvent, index = 0) {
		const clientX = this.getClientPosition(event, index).clientX;
		return clientX - this.elementPosition.left;
	}

	moveTop(event: TouchEvent, index = 0) {
		const clientY = this.getClientPosition(event, index).clientY;
		return clientY - this.elementPosition.top;
	}

	/*
	 * Detection
	 */

	centeringImage() {
		const img = this.element.getElementsByTagName(this.elementTarget)[0];
		const initialMoveX = this.moveX;
		const initialMoveY = this.moveY;

		if (this.moveY > 0) {
			this.moveY = 0;
		}
		if (this.moveX > 0) {
			this.moveX = 0;
		}

		if (img) {
			this.limitPanY();
			this.limitPanX();
		}
		if (img && this.scale < 1 && this.moveX < this.element.offsetWidth * (1 - this.scale)) {
			this.moveX = this.element.offsetWidth * (1 - this.scale);
		}

		return initialMoveX !== this.moveX || initialMoveY !== this.moveY;
	}

	limitPanY() {
		const imgHeight = this.getImageHeight();
		const scaledImgHeight = imgHeight * this.scale;
		const parentHeight = this.parentElement.offsetHeight;
		const elementHeight = this.element.offsetHeight;

		if (scaledImgHeight < parentHeight) {
			this.moveY = (parentHeight - elementHeight * this.scale) / 2;
		} else {
			const imgOffsetTop = ((imgHeight - elementHeight) * this.scale) / 2;

			if (this.moveY > imgOffsetTop) {
				this.moveY = imgOffsetTop;
			} else if (scaledImgHeight + Math.abs(imgOffsetTop) - parentHeight + this.moveY < 0) {
				this.moveY = -(scaledImgHeight + Math.abs(imgOffsetTop) - parentHeight);
			}
		}
	}

	limitPanX() {
		const imgWidth = this.getImageWidth();
		const scaledImgWidth = imgWidth * this.scale;
		const parentWidth = this.parentElement.offsetWidth;
		const elementWidth = this.element.offsetWidth;

		if (scaledImgWidth < parentWidth) {
			this.moveX = (parentWidth - elementWidth * this.scale) / 2;
		} else {
			const imgOffsetLeft = ((imgWidth - elementWidth) * this.scale) / 2;

			if (this.moveX > imgOffsetLeft) {
				this.moveX = imgOffsetLeft;
			} else if (scaledImgWidth + Math.abs(imgOffsetLeft) - parentWidth + this.moveX < 0) {
				this.moveX = -(imgWidth * this.scale + Math.abs(imgOffsetLeft) - parentWidth);
			}
		}
	}

	setBasicStyles() {
		this.element.style.display = 'flex';
		this.element.style.alignItems = 'center';
		this.element.style.justifyContent = 'center';
		this.element.style.transformOrigin = '0 0';
		this.setImageSize();
	}

	removeBasicStyles() {
		this.element.style.display = '';
		this.element.style.alignItems = '';
		this.element.style.justifyContent = '';
		this.element.style.transformOrigin = '';
		this.removeImageSize();
	}

	setImageSize() {
		const imgElement = <HTMLCollectionOf<HTMLElement>>(
			this.element.getElementsByTagName(this.elementTarget)
		);

		if (imgElement.length) {
			imgElement[0].style.maxWidth = '100%';
			imgElement[0].style.maxHeight = '100%';

			this.setAutoHeight();
		}
	}

	setAutoHeight() {
		const imgElement = <HTMLCollectionOf<HTMLElement>>(
			this.element.getElementsByTagName(this.elementTarget)
		);

		if (!this.properties.autoHeight || !imgElement.length) {
			return;
		}

		const imgNaturalWidth = parseInt(imgElement[0].getAttribute('width'), 10);
		const imgNaturalHeight = parseInt(imgElement[0].getAttribute('height'), 10);
		const sizeRatio = imgNaturalWidth / imgNaturalHeight;
		const parentWidth = this.parentElement.offsetWidth;

		imgElement[0].style.maxHeight = `${parentWidth / sizeRatio}px`;
	}

	removeImageSize() {
		const imgElement = <HTMLCollectionOf<HTMLElement>>(
			this.element.getElementsByTagName(this.elementTarget)
		);

		if (imgElement.length) {
			imgElement[0].style.maxWidth = '';
			imgElement[0].style.maxHeight = '';
		}
	}

	getElementPosition() {
		this.elementPosition = this.element.parentElement.getBoundingClientRect();
	}

	getTouchstartPosition(event: TouchEvent) {
		const { clientX, clientY } = this.getClientPosition(event);

		this.startX = clientX - this.elementPosition.left;
		this.startY = clientY - this.elementPosition.top;
	}

	getClientPosition(event: TouchEvent, index = 0) {
		let clientX: number;
		let clientY: number;

		if (event.type === 'touchstart' || event.type === 'touchmove') {
			clientX = event.touches[index].clientX;
			clientY = event.touches[index].clientY;
		}

		return {
			clientX,
			clientY
		};
	}

	resetScale() {
		this.scale = 1;
		this.moveX = 0;
		this.moveY = 0;
		this.updateInitialValues();
		this.transformElement(this.properties.transitionDuration);
	}

	updateInitialValues() {
		this.initialScale = this.scale;
		this.initialMoveX = this.moveX;
		this.initialMoveY = this.moveY;
	}

	getDistance(touches: TouchList) {
		return Math.sqrt(
			Math.pow(touches[0].pageX - touches[1].pageX, 2) +
				Math.pow(touches[0].pageY - touches[1].pageY, 2)
		);
	}

	getImageHeight() {
		const img = (<HTMLCollectionOf<HTMLElement>>(
			this.element.getElementsByTagName(this.elementTarget)
		))[0];
		return img.offsetHeight;
	}

	getImageWidth() {
		const img = (<HTMLCollectionOf<HTMLElement>>(
			this.element.getElementsByTagName(this.elementTarget)
		))[0];
		return img.offsetWidth;
	}

	transformElement(duration: number) {
		this.element.style.transition = `all ${duration}ms`;
		this.element.style.transform = `matrix(${Number(this.scale)}, 0, 0, ${Number(this.scale)}, ${Number(this.moveX)}, ${Number(this.moveY)})`;
	}

	pollLimitZoom() {
		const poll = setInterval(() => {
			if (this.getLimitZoom()) {
				clearInterval(poll);
			}
		}, 10);
	}

	/* Public properties and methods */

	public setMoveX(value: number, transitionDuration: number): void {
		this.moveX = value;
		this.transformElement(transitionDuration || this.properties.transitionDuration);
	}

	public setMoveY(value: number, transitionDuration: number): void {
		this.moveY = value;
		this.transformElement(transitionDuration || this.properties.transitionDuration);
	}

	public alignImage() {
		const isMoveChanged = this.centeringImage();

		if (isMoveChanged) {
			this.updateInitialValues();
			this.transformElement(this.properties.transitionDuration);
		}
	}

	public setTransform(properties: {
		scale?: number;
		transitionDuration?: number;
		x?: number;
		y?: number;
	}) {
		const transitionDuration =
			properties.transitionDuration || this.properties.transitionDuration;

		if (properties.x) {
			this.moveX = properties.x;
		}

		if (properties.y) {
			this.moveY = properties.y;
		}

		if (properties.scale) {
			this.scale = properties.scale;
		}

		this.updateInitialValues();
		this.transformElement(transitionDuration);
	}

	public destroy() {
		this.removeBasicStyles();
		this.touches.destroy();
	}
}
